import { createFileRoute, Link, useSearch } from "@tanstack/react-router";
import { Logo } from "@/components/brand/logo";
import { BrandButton } from "@/components/brand/brand-button";
import { AlertCircle, ArrowLeft } from "lucide-react";

const errorMessages: Record<string, { title: string; description: string }> = {
	unable_to_create_user: {
		title: "Account already exists",
		description:
			"An account with this email already exists. Try signing in with your email and password, or use a different authentication method.",
	},
	email_not_verified: {
		title: "Email not verified",
		description:
			"Please verify your email address before signing in. Check your inbox for the verification link.",
	},
	invalid_credentials: {
		title: "Invalid credentials",
		description:
			"The email or password you entered is incorrect. Please try again.",
	},
	session_expired: {
		title: "Session expired",
		description: "Your session has expired. Please sign in again to continue.",
	},
	access_denied: {
		title: "Access denied",
		description: "You don't have permission to access this resource.",
	},
	default: {
		title: "Authentication error",
		description:
			"Something went wrong during authentication. Please try again.",
	},
};

export const Route = createFileRoute("/api/auth/error")({
	component: AuthErrorPage,
	validateSearch: (search: Record<string, unknown>) => ({
		error: (search.error as string) || "default",
	}),
});

function AuthErrorPage() {
	const { error } = useSearch({ from: "/api/auth/error" });
	const errorInfo = errorMessages[error] || errorMessages.default;

	return (
		<div className="min-h-svh flex flex-col bg-background">
			{/* Header */}
			<div className="px-6 py-4 lg:px-12">
				<Logo size="sm" />
			</div>

			{/* Content */}
			<div className="flex-1 flex items-center justify-center px-6 py-12">
				<div className="w-full max-w-md">
					<div className="bg-card rounded-2xl p-8 shadow-lg 0 dark:shadow-black/20">
						{/* Error Icon */}
						<div className="flex justify-center mb-6">
							<div className="w-16 h-16 rounded-full bg-red-50 dark:bg-red-900/30 flex items-center justify-center">
								<AlertCircle className="w-8 h-8 text-red-500" />
							</div>
						</div>

						{/* Error Message */}
						<div className="text-center mb-8">
							<h1 className="text-2xl font-bold text-foreground mb-2">
								{errorInfo.title}
							</h1>
							<p className="text-muted-foreground">{errorInfo.description}</p>
						</div>

						{/* Error Code */}
						{error !== "default" && (
							<div className="mb-6 p-3 bg-muted rounded-lg">
								<p className="text-xs text-muted-foreground/60 text-center font-mono">
									Error code: {error}
								</p>
							</div>
						)}

						{/* Actions */}
						<div className="space-y-3">
							<Link to="/login" className="block">
								<BrandButton className="w-full">Sign in</BrandButton>
							</Link>
							<Link to="/signup" className="block">
								<BrandButton variant="secondary" className="w-full">
									Create account
								</BrandButton>
							</Link>
						</div>

						{/* Back Link */}
						<div className="mt-6 text-center">
							<Link
								to="/"
								className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors"
							>
								<ArrowLeft className="w-4 h-4" />
								Back to home
							</Link>
						</div>
					</div>
				</div>
			</div>
		</div>
	);
}
